/**
 * 表示名解決ヘルパ (`docs/guidelines/frontend.md` 「ユーザー名はニックネーム優先」)。
 *
 * Firestore admin が手入力した target.displayName や、本名カラムなど別の名前列を持つ
 * レコードに対して、`useMemberList()` 経由で得られる email→nickName Map で
 * ニックネームに resolve する。fallback は元のレコードの最良名 → email。
 *
 * 本名と並記する UI は禁止 (ガイドライン)。出口で fallback コードを書かずに済むよう、
 * このヘルパで 1 値に絞ってから render すること。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business email -> nickName Map を持って画面側の displayName をニックネーム優先に揃える共通ロジック
 * @graph-connects none
 */

import type { Member } from '../types/api.js';

/** @graph-connects none */
export function buildNicknameByEmail(members: Member[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!members) return map;
  for (const m of members) {
    if (m.nickName !== null && m.nickName.length > 0) {
      map.set(m.email.toLowerCase(), m.nickName);
    }
  }
  return map;
}

/**
 * email から表示名 (ニックネーム優先) を解決する。
 * Firestore admin が手入力した target.displayName は本名のことが多いため、
 * `users.nickName` で resolve し直してニックネーム優先に揃える。
 *
 * @param nicknameByEmail `buildNicknameByEmail()` の戻り値
 * @param target email / 旧 displayName を持つレコード
 * @graph-connects none
 */
export function resolveDisplayNameByEmail(
  nicknameByEmail: Map<string, string>,
  target: { email?: string | null; displayName?: string | null },
): string {
  const email = target.email;
  if (email) {
    const fromMap = nicknameByEmail.get(email.toLowerCase());
    if (fromMap) return fromMap;
  }
  if (
    target.displayName !== undefined &&
    target.displayName !== null &&
    target.displayName.length > 0
  ) {
    return target.displayName;
  }
  return email ?? '';
}

/**
 * MemberListResponse.{engineers, qa} のフラット配列を input にとり、
 * `email -> nickName || name` の resolve Map を返す。projects.tsx 互換。
 *
 * @graph-connects none
 */
export function buildTeamMemberDisplayNames(members: Member[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!members) return map;
  for (const m of members) {
    map.set(m.email.toLowerCase(), m.nickName?.length ? m.nickName : m.name);
  }
  return map;
}

/**
 * teamMemberDisplayNames Map から email → 表示名 (ニックネーム優先) を解決する。
 * 未収録時は member.name に fallback。projects.tsx 互換。
 *
 * @graph-connects none
 */
export function resolveTeamMemberDisplayName(
  teamMemberDisplayNames: Map<string, string> | null,
  member: { email: string; name: string },
): string {
  return teamMemberDisplayNames?.get(member.email.toLowerCase()) ?? member.name;
}
